import { useState } from "react";
import {
  Box, Typography, Paper, TextField, InputAdornment, Alert, Stack, Button, Chip,
  IconButton, Menu, MenuItem, ListItemIcon, ListItemText,
} from "@mui/material";
import { DataGrid, type GridColDef, type GridPaginationModel, type GridRowParams } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { itemsApi, type ItemDto } from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { useAuth } from "@/store/auth";
import { useTaxRegime } from "@/shared/hooks/useTaxRegime";
import { ItemForm } from "./ItemForm";

export function ItemsPage() {
  const [search, setSearch] = useState("");
  const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 25 });
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<ItemDto | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuRow, setMenuRow] = useState<ItemDto | null>(null);

  const canCreate = useAuth(s => s.hasPermission("inventory.item.create"));
  const canUpdate = useAuth(s => s.hasPermission("inventory.item.edit"));
  const canDelete = useAuth(s => s.hasPermission("inventory.item.delete"));
  const { taxLabel } = useTaxRegime();
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ["items", search, pagination.page, pagination.pageSize],
    queryFn: () => itemsApi.list({
      search: search || undefined,
      page: pagination.page + 1, pageSize: pagination.pageSize,
    }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => itemsApi.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["items"] }),
  });

  const openCreate = () => { setEditing(null); setFormOpen(true); };
  const openEdit = (i: ItemDto) => { setEditing(i); setFormOpen(true); setMenuAnchor(null); };
  const closeMenu = () => { setMenuAnchor(null); setMenuRow(null); };
  const handleDelete = (i: ItemDto) => {
    closeMenu();
    if (confirm(`Delete item "${i.name}"? Blocked if any inventory movement exists for this item.`)) {
      deleteMutation.mutate(i.id);
    }
  };

  const columns: GridColDef<ItemDto>[] = [
    { field: "sku", headerName: "SKU", width: 120 },
    { field: "name", headerName: "Name", flex: 1, minWidth: 220 },
    { field: "genericName", headerName: "Generic", flex: 1, minWidth: 160 },
    { field: "itemType", headerName: "Type", width: 100 },
    { field: "form", headerName: "Form", width: 90 },
    { field: "strength", headerName: "Strength", width: 100 },
    { field: "manufacturer", headerName: "Manufacturer", width: 160 },
    {
      field: "isScheduleDrug", headerName: "Schedule", width: 110,
      renderCell: (p) => p.value
        ? <Chip size="small" color="warning" label={`Schedule ${p.row.scheduleCategory ?? ""}`.trim()} />
        : null,
    },
    {
      field: "gstRate", headerName: `${taxLabel}%`, width: 80, type: "number",
      valueFormatter: (v) => v == null ? "—" : `${v}%`,
    },
    {
      field: "actions", headerName: "", width: 60, sortable: false, filterable: false,
      renderCell: (p) => (
        <IconButton size="small" onClick={(e) => { setMenuAnchor(e.currentTarget); setMenuRow(p.row); }}>
          <MoreVertIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight={600}>Items</Typography>
        {canCreate && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            New Item
          </Button>
        )}
      </Stack>

      <Paper sx={{ p: 2 }}>
        <TextField
          placeholder="Search by SKU, name, or generic name"
          fullWidth size="small" value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ mb: 2 }}
        />
        {query.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(query.error)}</Alert>}
        {deleteMutation.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(deleteMutation.error)}</Alert>}
        <Box sx={{ height: 600 }}>
          <DataGrid
            rows={query.data?.items ?? []}
            columns={columns}
            loading={query.isLoading}
            paginationMode="server"
            rowCount={query.data?.totalCount ?? 0}
            paginationModel={pagination}
            onPaginationModelChange={setPagination}
            pageSizeOptions={[10, 25, 50, 100]}
            disableRowSelectionOnClick
            getRowId={(row) => row.id}
            onRowDoubleClick={(p: GridRowParams<ItemDto>) => canUpdate && openEdit(p.row)}
          />
        </Box>
      </Paper>

      <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={closeMenu}>
        {canUpdate && (
          <MenuItem onClick={() => menuRow && openEdit(menuRow)}>
            <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Edit</ListItemText>
          </MenuItem>
        )}
        {canDelete && (
          <MenuItem onClick={() => menuRow && handleDelete(menuRow)}>
            <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
            <ListItemText sx={{ color: "error.main" }}>Delete</ListItemText>
          </MenuItem>
        )}
      </Menu>

      <ItemForm open={formOpen} onClose={() => setFormOpen(false)} item={editing} />
    </Box>
  );
}
